/* eslint-disable no-restricted-imports -- file uses shared helpers from auth-fe to parse data loader responses */
import { skipToken } from '@apollo/client/react';
import type {
  AuthenticationError,
  LoginCredentials,
  LoginResult,
  TrackError,
} from '@opendoor/auth-fe';
import { DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE, parseAuthentication } from '@opendoor/auth-fe';
import { useRevalidator, useRouteLoaderData } from '@remix-run/react';
import type { loader } from 'root';

import { DevError, UnauthenticatedError } from 'platform/Error';

import { COSMOS_DOMAIN } from 'shared/helpers/globals';
import { LOGOUT_URL } from 'shared/helpers/routes';

import { UserSessionDocument } from './__generated__/userSession.query';
import { useQuery } from './apollo';
import navigationHelpers from './navigationHelpers';
import { globalTrackError, useObservability } from './observability';

const AUTH_MISSING_FROM_ROUTING_TREE_ERROR = 'Auth loader does not exist in the the routing tree';

const MISSING_AUTH = {
  state: 'error',
  message: AUTH_MISSING_FROM_ROUTING_TREE_ERROR,
} as AuthenticationError;

/**
 * Helper to return the user regardless of auth state
 * @module platform/auth-fe
 * @returns {user | undefined} - The authenticated user
 */
export const useAuth = () => {
  const rootLoaderData = useRouteLoaderData<typeof loader>('root');
  const revalidator = useRevalidator();
  const { trackError } = useObservability();

  const auth = rootLoaderData?.authentication ?? MISSING_AUTH;

  return {
    ...auth,
    /**
     * NOTE: This is a client-side revalidation method that can be used to refresh the auth state outside of normal data mutations like window focus or polling on an interval. It also re-validates all loaders in the current route's routing tree
     * @see https://remix.run/docs/en/main/hooks/use-revalidator
     *
     * TODO( (@seller-engineering)): Investigate alternate ways of refreshing auth state that don't require revalidating all loaders in the routing tree. This could be a performance concern if the routing tree is large or the data is expensive to fetch.
     * Currently only called for the closing re-auth modal, so it's not a big deal but could be in the future.
     */
    refresh: revalidator.revalidate,
    logout,
    verifyLogin: (credentials: LoginCredentials) => verifyLogin(trackError, credentials),
  };
};

/**
 * Helper to return the authenticated user for auth-gated routes
 * @module platform/auth-fe
 * @throws {SystemError} - If the user is not authenticated
 */
export const useAuthAuthenticated = (triggeredQuery?: string) => {
  const value = useAuth();
  if (value.state === 'error') {
    throw new DevError(value.message);
  }
  if (value.state !== 'authenticated' || !value.user) {
    throw new UnauthenticatedError(triggeredQuery, 'app-code');
  }

  return value;
};

/**
 * Lightweight hook to fetch auth client-side. This should only be used in error
 * boundaries or other places where the user's auth state is needed but the
 * component/page doesn't have the auth loader somewhere above it in the routing tree.
 * All routes should use the auth loader data fetched server-side instead.
 * This is primarily used for the error boundary and components rendered withn it
 */
export const useAuthWithClientSideFallback = () => {
  const { trackError } = useObservability();
  const serverSideAuth = useAuth();
  // Explicltly only call query if SSR data doesn't exist
  const shouldFetchClientside =
    !serverSideAuth ||
    (serverSideAuth.state === 'error' &&
      serverSideAuth.message === AUTH_MISSING_FROM_ROUTING_TREE_ERROR);
  const response = useQuery(UserSessionDocument, shouldFetchClientside ? undefined : skipToken);

  // Uses SSR auth if it's available and didn't require a client-side fetch
  if (serverSideAuth.state !== 'error' || !shouldFetchClientside) {
    return serverSideAuth;
  }

  const authData = parseAuthentication(response, trackError);
  return {
    ...serverSideAuth,
    ...authData,
  };
};

/**
 * Verifies the user's login credentials
 * @param trackError - Function to track errors
 * @param credentials - The user's login credentials
 */
async function verifyLogin(
  trackError: TrackError,
  credentials: LoginCredentials,
): Promise<LoginResult> {
  let payload: unknown;
  if ('googleIdToken' in credentials) {
    payload = { google_id_token: credentials.googleIdToken };
  } else if ('facebookIdToken' in credentials) {
    payload = { facebook_id_token: credentials.facebookIdToken };
  } else {
    payload = {
      human: {
        email: credentials.email,
        password: credentials.password,
      },
    };
  }

  /**
   * See `verify_login` at
   * https://github.com/opendoor-labs/web/blob/master/app/controllers/humans/sessions_controller.rb
   * Uses domain from https://github.com/opendoor-labs/code/blob/master/js/packages/auth-fe/src/globals.ts#L1-L7
   */
  const resp = await fetch(`${COSMOS_DOMAIN}/verify_login`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (resp.ok) {
    return { success: true };
  }
  let displayErrorMessage = DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE;
  try {
    const json = await resp.json();
    if ('error' in json) {
      displayErrorMessage = json['error'];
    }
  } catch (e) {
    trackError('login error without error message', e as Error);
  }
  return {
    success: false,
    displayErrorMessage,
  };
}

/**
 * Logs the user out and reloads the page
 *
 * Uses domain from https://github.com/opendoor-labs/code/blob/master/js/packages/auth-fe/src/globals.ts#L1-L7
 */
const logout = () =>
  fetch(`${COSMOS_DOMAIN}/session/sign-out`, { method: 'GET', credentials: 'include' })
    .then(() => {
      navigationHelpers.hardPageReload();
    })
    .catch((e) => {
      globalTrackError('logout', e);
      navigationHelpers.hardNavAssign(LOGOUT_URL);
    });

/**
 * Helper to sign the user out of all devices
 */
export const signOutOfAllDevices = async () => {
  try {
    const resp = await fetch('/session/remote-sign-out').then((res) => res.json());
    if (resp.signed_out === true || resp.logged_in === false) {
      navigationHelpers.hardPageReload();
    }
  } catch (e) {
    globalTrackError('Unable to sign out of all devices', e as Error);
  }
};
